import React from "react";
import * as PD from '../classes/pdStyle';
import Buffering from './Buffering';
import dsd from '../classes/clsDSD';
import { getSerialization } from '../utils/utilsDataSpec';

// ---------------------------------------------------------------------------
// Tree flattening
// ---------------------------------------------------------------------------

// Walk an Element node and collect every descendant as an absolute path.
// Returns [{ path, displayPath, isArray }]
function flattenElement(element, prefix = '') {

    const results = [];
    if (!element) { return results; }

    const children = element.Children || [];

    for (const child of children) {
        const label = child.label || child.name || '';
        if (!label) { continue; }
        const path = prefix ? `${prefix}/${label}` : label;
        results.push({ path, isArray: child.occurs === 'many' });
        results.push(...flattenElement(child, path));
    }

    // Also handle Class.Properties and direct Properties (complex types)
    const propSets = [];
    if (element.Class && element.Class.Properties && element.Class.Properties.length) {
        propSets.push(element.Class.Properties);
    }
    if (element.Properties && element.Properties.length) {
        propSets.push(element.Properties);
    }

    for (const props of propSets) {
        for (const prop of props) {
            const name = prop.name || '';
            if (!name) { continue; }
            const path = prefix ? `${prefix}/${name}` : name;
            results.push({ path, isArray: false });
            if (prop.complex && prop.Properties && prop.Properties.length) {
                results.push(...flattenElement({ Children: [], Properties: prop.Properties }, path));
            }
        }
    }

    return results;
}

// Return all paths relative to rootPath.
// When rootPath is blank, all paths are returned with their full path as the value.
// When rootPath is set, only descendant paths are returned, stripped of the rootPath prefix.
function pathsRelativeTo(allPaths, rootPath) {
    if (!rootPath) {
        return allPaths.map(p => ({ value: p.path, label: p.path, isArray: p.isArray }));
    }
    const prefix = rootPath + '/';
    return allPaths
        .filter(p => p.path.startsWith(prefix))
        .map(p => ({
            value: p.path.slice(prefix.length),
            label: p.path.slice(prefix.length),
            isArray: p.isArray,
        }));
}

// ---------------------------------------------------------------------------
// Mapping state shape
// ---------------------------------------------------------------------------

const EMPTY_MAPPING = {
    listRoot:         '',
    listName:         '',
    listDescription:  '',
    listNamespace:    '',
    listPrefix:       '',
    itemsRoot:        '',
    itemsCode:        '',
    itemsLabel:       '',
    itemsDefinition:  '',
    itemsUsageNotes:  '',
    itemsParentsRoot: '',
    itemsParentsCode: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

class ImportCodeListMapping extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            renderState: null,
            Serialization: null,
            errorMessage: null,
            mapping: { ...EMPTY_MAPPING },
        };
        this.dsd = new dsd();
        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        if (this.props.idSerialization) {
            this.load(this.props.idSerialization);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.idSerialization !== this.props.idSerialization) {
            if (this.props.idSerialization) {
                this.load(this.props.idSerialization);
            } else {
                this.setState({ renderState: null, Serialization: null, mapping: { ...EMPTY_MAPPING } });
            }
        }
    }

    load(id) {
        this.setState({ renderState: 'loading', Serialization: null, errorMessage: null });
        getSerialization(this.dsd, id,
            (Serialization) => {
                this.setState({ Serialization, renderState: 'loaded', mapping: { ...EMPTY_MAPPING } });
            },
            (err) => {
                this.setState({ renderState: 'error', errorMessage: (err && err.message) || 'Failed to load serialization' });
            }
        );
    }

    handleChange(event) {
        const { name, value } = event.target;
        this.setState(prev => {
            const mapping = { ...prev.mapping, [name]: value };

            // Reset downstream fields when a root changes
            if (name === 'listRoot') {
                mapping.listName        = '';
                mapping.listDescription = '';
                mapping.listNamespace   = '';
                mapping.listPrefix      = '';
            }
            if (name === 'itemsRoot') {
                mapping.itemsCode        = '';
                mapping.itemsLabel       = '';
                mapping.itemsDefinition  = '';
                mapping.itemsUsageNotes  = '';
                mapping.itemsParentsRoot = '';
                mapping.itemsParentsCode = '';
            }
            if (name === 'itemsParentsRoot') {
                mapping.itemsParentsCode = '';
            }

            if (this.props.onChange) {
                this.props.onChange(mapping);
            }

            return { mapping };
        });
    }

    renderSelect(label, name, options, value, style = {}) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, ...style }}>
                <label style={{ display: 'inline-block', width: 140, fontWeight: 'bold', flexShrink: 0 }}>
                    {label}
                </label>
                <select name={name} value={value || ''} onChange={this.handleChange} style={{ minWidth: 220 }}>
                    <option value=""></option>
                    {options.map((opt, i) => (
                        <option key={i} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    render() {

        const { renderState, Serialization, errorMessage, mapping } = this.state;

        if (!this.props.idSerialization) { return null; }
        if (renderState === 'loading') { return <Buffering />; }
        if (renderState === 'error') { return <PD.ErrorBox>{errorMessage}</PD.ErrorBox>; }
        if (renderState !== 'loaded' || !Serialization) { return null; }

        if (!Serialization.Root) {
            return <p style={{ color: '#888' }}>This serialization has no structure defined.</p>;
        }
        const allPaths = flattenElement(Serialization.Root);

        // List root: any path in the serialization
        const listRootOptions = allPaths.map(p => ({ value: p.path, label: p.path, isArray: p.isArray }));

        // List field paths: all descendants relative to list root
        const listFieldOptions = pathsRelativeTo(allPaths, mapping.listRoot)
            .filter(p => !p.isArray);

        // Items root: must be an array
        const itemsRootOptions = allPaths
            .filter(p => p.isArray)
            .map(p => ({ value: p.path, label: p.path }));

        // Items field paths: descendants relative to items root  
        const itemsRelative = pathsRelativeTo(allPaths, mapping.itemsRoot);
        const itemsFieldOptions = itemsRelative.filter(p => !p.isArray);

        // Parents root: arrays relative to items root
        const parentsRootOptions = itemsRelative.filter(p => p.isArray);

        // Parents field paths: descendants relative to <itemsRoot>/<parentsRoot>
        const parentsFullRoot = [mapping.itemsRoot, mapping.itemsParentsRoot]
            .filter(Boolean).join('/');
        const parentsFieldOptions = pathsRelativeTo(allPaths, parentsFullRoot)
            .filter(p => !p.isArray);

        return (
            <div style={{ marginTop: 12 }}>

                <fieldset style={{ marginBottom: 12 }}>
                    <legend><strong>List</strong></legend>
                    {this.renderSelect('start at', 'listRoot', listRootOptions, mapping.listRoot)}
                    <hr style={{ margin: '8px 0' }} />
                    {this.renderSelect('Name',           'listName',        listFieldOptions, mapping.listName)}
                    {this.renderSelect('Description',    'listDescription', listFieldOptions, mapping.listDescription)}
                    {this.renderSelect('Namespace',      'listNamespace',   listFieldOptions, mapping.listNamespace)}
                    {this.renderSelect('Default Prefix', 'listPrefix',      listFieldOptions, mapping.listPrefix)}
                </fieldset>

                <fieldset>
                    <legend><strong>Items</strong></legend>
                    {this.renderSelect('start at', 'itemsRoot', itemsRootOptions, mapping.itemsRoot)}
                    <hr style={{ margin: '8px 0' }} />
                    {this.renderSelect('Code',        'itemsCode',        itemsFieldOptions, mapping.itemsCode)}
                    {this.renderSelect('Label',       'itemsLabel',       itemsFieldOptions, mapping.itemsLabel)}
                    {this.renderSelect('Definition',  'itemsDefinition',  itemsFieldOptions, mapping.itemsDefinition)}
                    {this.renderSelect('Usage Notes', 'itemsUsageNotes',  itemsFieldOptions, mapping.itemsUsageNotes)}
                    <fieldset style={{ marginTop: 8 }}>
                        <legend>Parents</legend>
                        {this.renderSelect('start at', 'itemsParentsRoot', parentsRootOptions, mapping.itemsParentsRoot)}
                        <hr style={{ margin: '8px 0' }} />
                        {this.renderSelect('Code', 'itemsParentsCode', parentsFieldOptions, mapping.itemsParentsCode)}
                    </fieldset>
                </fieldset>

            </div>
        );
    }

}

export default ImportCodeListMapping;
